import React, { useMemo } from 'react'
import { Formik, FormikHelpers, FormikProps } from 'formik'
import * as Yup from 'yup'
import * as R from 'ramda'

import ITrustedApplication, {
  IFullTrustedApplication,
} from 'domains/trustedApps/models/ITrustedApplication'
import {
  ModalWrapper,
  WYSIWYGEditor,
  DatePicker,
  FormInput,
} from 'lib/components'
import { IAddTrustedAppSelection } from 'domains/trustedApps/components/AddTrustedApps/models/IAddTrustedAppSelection'
import FormLabel from 'lib/components/modern/FormLabel'
import { TRUSTED_APPS_RECORD } from 'lib/records/modules/trustedApplications'
import { showToast, SUCCESS_TOAST } from 'lib/utils/toast'
import { updateRecord } from 'lib/records/workflows/updateRecord'
import { utils } from '@ims/1edtech-frontend-common'
import { useQueryTrustedAppsTags } from 'domains/trustedApps/hooks/useQueryTrustedAppsTags'
import { useQueryTrustedAppsSurveys } from 'domains/trustedApps/hooks/useQueryTrustedAppsSurveys'
import { ERROR_TOAST } from '../../../lib/utils/toast'
import {
  GetTrustedAppSurveyQuestionsResponse,
  TrustedAppSurveyResponse,
} from 'domains/trustedApps/models/ITrustedAppsCustomQuestions'
import FormSelect from 'lib/components/modern/Select/FormSelect'
import { useUpdateCustomQuestions } from 'domains/trustedApps/hooks/useUpdateCustomQuestions'
import WidgetHeader from 'domains/dashboard/components/WidgetHeader'

type FormValues = {
  technicalNotes: string
  instructionalNotes: string
  licensingExpirationDate: Date | undefined
  licensingFees: string | undefined
  currentApplicationTag: string | undefined
  tags: string[] | undefined
  customQuestions: Record<
    number,
    Array<{
      responseId: number | null
      responseText?: string
    }>
  >
}

const validationSchema = Yup.object().shape({
  technicalNotes: Yup.string(),
  instructionalNotes: Yup.string(),
  licensingExpirationDate: Yup.date().nullable(),
  licensingFees: Yup.string().nullable(),
  tags: Yup.array().of(Yup.string()),
  currentApplicationTag: Yup.string(),
  customQuestions: Yup.object(),
})

type Props = {
  app?:
    | ITrustedApplication
    | IFullTrustedApplication
    | (IAddTrustedAppSelection & {
        title: string
        application?: any
      })
  isOpen: boolean
  onRequestClose: (saved?: boolean) => void
  onAppSaved?: (app: IAddTrustedAppSelection) => any
}

export default function EditAdditionalInfoModal(props: Props) {
  const tagsQuery = useQueryTrustedAppsTags()
  const surveysQuery = useQueryTrustedAppsSurveys()
  const [updateQuestions] = useUpdateCustomQuestions()

  const appTitle = `${
    props.app?.title ||
    R.pathOr('Application', ['application', 'productName'], props.app)
  }`.trim()

  const onSubmit = async (
    values: FormValues,
    bag: FormikHelpers<FormValues>,
  ) => {
    const updatedQuestions = await updateQuestions({
      questions: surveysQuery.data!.map((question) => {
        const newQuestionResponses = values.customQuestions[question.id].filter(
          (response) => response.responseId === null,
        )

        return {
          ...question,
          id: question.id,
          responses: [
            ...(question.responses ?? []),
            ...newQuestionResponses.map((response) => ({
              responseText: response.responseText ?? '',
            })),
          ],
        }
      }),
    })

    const flushNotes = (text: string) => {
      return text === '<p><br></p>' ? text.replace(/<[^>]*>/g, '').trim() : text
    }

    const app = {
      ...props.app,
      instructionalNotes: flushNotes(values.instructionalNotes),
      technicalNotes: flushNotes(values.technicalNotes),
      licensingFees: values.licensingFees,
      licensingExpirationDate: values.licensingExpirationDate
        ? utils.date
            .getMoment(values.licensingExpirationDate)
            .startOf('day')
            .tz('America/New_York')
            .valueOf()
        : null,
      surveyResults: surveysQuery.data?.map((question: any) => ({
        id: question.id,
        questionText: question.questionText,
        questionType: question.questionType,
        hideFromTeachers: question.hideFromTeachers,
        responses: values.customQuestions[question.id].map((response) => {
          if (response.responseId === null) {
            const updatedQuestion = updatedQuestions.questions.find(
              (q) => q.id === question.id,
            )

            const responseId = updatedQuestion?.responses?.find(
              (r) => r.responseText === response.responseText,
            )?.id

            return {
              id: responseId,
              responseText: response.responseText,
            }
          }

          return {
            id: response.responseId,
            responseText: question.responses?.find(
              (r: TrustedAppSurveyResponse) => r.id === response.responseId,
            )?.responseText,
          }
        }),
      })),
      tags: values.tags,
    }

    if (props.onAppSaved) {
      props.onAppSaved(app as IAddTrustedAppSelection)
      bag.setSubmitting(false)
      props.onRequestClose(true)
    } else if (props.app) {
      const { success } = await updateRecord(
        TRUSTED_APPS_RECORD,
        (props.app as ITrustedApplication).id,
        app,
        true,
      )

      await tagsQuery.refetch()

      bag.setSubmitting(false)

      if (success) {
        props.onRequestClose(true)
        showToast(
          SUCCESS_TOAST,
          `Successfully saved additional info for ${appTitle}.`,
        )
      } else {
        showToast(ERROR_TOAST, 'Failed to update app. Please try again.')
      }
    }
  }

  const initialCustomQuestions = useMemo((): FormValues['customQuestions'] => {
    if (surveysQuery.data) {
      return surveysQuery.data.reduce((acc, s) => {
        const surveyResult = props.app?.surveyResults?.find(
          (r) => r.id === s.id,
        )

        if (surveyResult) {
          acc[s.id] = surveyResult.responses.map((r) => ({
            responseId: r.id,
          }))
        } else {
          acc[s.id] = []
        }

        return acc
      }, {} as FormValues['customQuestions'])
    }

    return {}
  }, [surveysQuery.data, props.app?.surveyResults])

  const initialValues: FormValues = useMemo(
    () => ({
      technicalNotes: props.app?.technicalNotes || '',
      instructionalNotes: props.app?.instructionalNotes || '',
      licensingExpirationDate: props.app?.licensingExpirationDate
        ? utils.date.getMoment(props.app.licensingExpirationDate).toDate()
        : undefined,
      licensingFees: props.app?.licensingFees,
      customQuestions: initialCustomQuestions,
      tags: props.app?.tags ?? [],
      currentApplicationTag: '',
    }),
    [initialCustomQuestions, props.app],
  )

  const renderQuestion = (
    question: GetTrustedAppSurveyQuestionsResponse['questions'][number],
    formik: FormikProps<FormValues>,
  ) => {
    const responses = question.responses || []

    if (responses.length > 0) {
      responses.sort((a, b) => a.responseText.localeCompare(b.responseText))
    }

    const getPlaceholder = (): string => {
      switch (question.questionType) {
        case 'SINGLE_RESPONSE_FROM_LIST':
          return 'Select one'
        case 'SINGLE_RESPONSE_FROM_LIST_UPDATABLE':
          return 'Select one or add your own'
        case 'MULTIPLE_RESPONSES_FROM_LIST_UPDATABLE':
          return 'Select or add your own'
        default:
          return 'Select'
      }
    }

    const placeholder: string = getPlaceholder()

    const currentResponses = formik.values.customQuestions[question.id] || []

    const setQuestionResponses = (
      responses: FormValues['customQuestions'][number],
    ) => {
      formik.setFieldValue('customQuestions', {
        ...formik.values.customQuestions,
        [question.id]: responses,
      })
    }

    const addExtendedResponse = (value: string) => {
      const newResponse = {
        responseId: null,
        responseText: value,
      }

      setQuestionResponses([...currentResponses, newResponse])
    }

    const isMultiselect = question.questionType.includes('MULTIPLE_RESPONSE')

    const selected = isMultiselect ? currentResponses : currentResponses[0]

    const customOptions = currentResponses
      .filter((resp) => resp.responseId === null && resp.responseText)
      .map((resp) => ({
        label: resp.responseText || 'Custom option',
        value: `custom-${resp.responseText}`,
      }))

    const allOptions = [
      ...responses.map((s) => ({
        label: s.responseText,
        value: s.id,
      })),
      ...customOptions,
    ]

    const customValueMap = customOptions.reduce((acc, opt) => {
      const key = opt.value as string
      const responseText = opt.label
      acc[key] = responseText
      return acc
    }, {} as Record<string, string>)

    const selectedValues = Array.isArray(selected)
      ? selected.map((s) => {
          if (s.responseId === null && s.responseText) {
            return `custom-${s.responseText}`
          }
          return s.responseId
        })
      : selected?.responseId === null && selected?.responseText
      ? `custom-${selected.responseText}`
      : selected?.responseId || null

    const sortedSelectedValues = Array.isArray(selectedValues)
      ? selectedValues.sort((id1, id2) => {
          const opt1 = allOptions.find((opt) => opt.value === id1)
          const opt2 = allOptions.find((opt) => opt.value === id2)

          if (opt1 && opt2) {
            return opt1.label.localeCompare(opt2.label)
          }

          return 0
        })
      : []

    return isMultiselect ? (
      <FormSelect
        label={question.questionText}
        name={`customQuestions.${question.id}`}
        selected={sortedSelectedValues}
        onChange={(value: any) => {
          if (!value) {
            setQuestionResponses([])
            return
          }

          if (typeof value === 'string' && !value.startsWith('custom-')) {
            addExtendedResponse(value)
            return
          }

          if (Array.isArray(value)) {
            const responses = value.map((v) => {
              if (typeof v === 'string' && !v.startsWith('custom-')) {
                return {
                  responseId: null,
                  responseText: v,
                }
              } else if (typeof v === 'string' && v.startsWith('custom-')) {
                const responseText = customValueMap[v]
                return {
                  responseId: null,
                  responseText,
                }
              } else {
                return {
                  responseId: v,
                }
              }
            })

            setQuestionResponses(responses)
          }
        }}
        placeholder={placeholder}
        options={allOptions}
        extensible={question.questionType.includes('LIST_UPDATABLE')}
        multiple={true}
        key={question.id}
      />
    ) : (
      <FormSelect
        label={question.questionText}
        name={`customQuestions.${question.id}`}
        selected={selectedValues}
        onChange={(value: any) => {
          if (!value) {
            setQuestionResponses([])
            return
          }

          if (typeof value === 'string' && !value.startsWith('custom-')) {
            setQuestionResponses([
              {
                responseId: null,
                responseText: value,
              },
            ])
            return
          }

          if (typeof value === 'string' && value.startsWith('custom-')) {
            const responseText = customValueMap[value]
            setQuestionResponses([
              {
                responseId: null,
                responseText,
              },
            ])
            return
          }

          setQuestionResponses([{ responseId: value }])
        }}
        placeholder={placeholder}
        options={allOptions}
        extensible={question.questionType.includes('LIST_UPDATABLE')}
        key={question.id}
      />
    )
  }

  const loading = tagsQuery.loading && surveysQuery.loading

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {(formik) => {
        return (
          <ModalWrapper
            isOpen={props.isOpen}
            title={`${appTitle} - Additional Info`}
            actions={
              loading
                ? []
                : [
                    {
                      text: 'Save',
                      variant: 'start',
                      extra: { type: 'submit' },
                      onClick: async () => {
                        await formik.submitForm()
                        formik.resetForm()
                      },
                    },
                    {
                      text: 'Cancel',
                      variant: 'neutral',
                      onClick: () => {
                        props.onRequestClose()
                        formik.resetForm()
                      },
                    },
                  ]
            }
            pending={formik.isSubmitting}
            maxWidth={['100%', 820, 900]}
          >
            <div className="flex flex-col gap-8 bg-white pb-16">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <FormLabel htmlFor="instructionalNotes">
                    Technical Notes
                  </FormLabel>
                  <WYSIWYGEditor
                    name="technicalNotes"
                    value={formik.values.technicalNotes}
                    onChange={(value: string) =>
                      formik.setFieldValue('technicalNotes', value)
                    }
                    placeholder="Technical Notes, Resource URLs, etc."
                  />
                </div>

                <div className="col-span-2">
                  <FormLabel htmlFor="instructionalNotes">
                    Instructional Notes
                  </FormLabel>
                  <WYSIWYGEditor
                    name="instructionalNotes"
                    value={formik.values.instructionalNotes}
                    onChange={(value: string) =>
                      formik.setFieldValue('instructionalNotes', value)
                    }
                    placeholder="Instructional Notes, Resource URLs, etc."
                  />
                </div>

                <div className="col-span-1">
                  <FormInput
                    label="Licensing Fees"
                    name="licensingFees"
                    placeholder="e.g. $100.00"
                    value={formik.values.licensingFees || ''}
                    handleChange={formik.handleChange}
                    valueDataTest="licensing-fee-field"
                  />
                </div>

                <div className="col-span-1">
                  <FormLabel htmlFor="licensingExpirationDate">
                    Licensing Expiration Date
                  </FormLabel>
                  <DatePicker
                    date={formik.values.licensingExpirationDate!}
                    handleChange={(value: Date) => {
                      formik.setFieldValue('licensingExpirationDate', value)
                    }}
                    isClearable={true}
                    openToDate={utils.date.getMoment().toDate()}
                    data-test="licensing-expiration-date-input"
                  />
                </div>

                <div className="col-span-2">
                  <FormSelect
                    label="Application Tags"
                    name="tags"
                    selected={
                      formik.values.tags &&
                      formik.values.tags.sort((a, b) => a.localeCompare(b))
                    }
                    onChange={(value: any) => {
                      formik.setFieldValue('tags', value)
                    }}
                    placeholder="Select or add your own"
                    options={(tagsQuery.data ?? [])
                      .map((s) => ({ label: s, value: s }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                    multiple
                    extensible
                  />
                </div>
              </div>

              <div>
                <WidgetHeader
                  title="TrustEd App Questions & Answers"
                  className="mb-4"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {surveysQuery.data
                    ?.sort((a: any, b: any) =>
                      a.questionText.localeCompare(b.questionText),
                    )
                    .map((question: any) => renderQuestion(question, formik))}
                </div>
              </div>
            </div>
          </ModalWrapper>
        )
      }}
    </Formik>
  )
}
